import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReadinessChip } from './ReadinessChip';
import { useTranslation } from 'react-i18next';
import {
  useConsignmentInvoiceCandidates, useFetchOrderDetails, useOrder, useOrderConsignmentInvoices,
  useOrderPaymentsAdmin, useOrders, useOrderTransition, useReviseOrderLines, useSendOrderMessage, useVerifyPayment,
  type LineRevision,
} from '../api/hooks';
import { ApiError } from '../api/client';
import { useSession } from '../auth/session';
import { downloadOrdersWorkbook, exportFileName, summaryFromDetail } from './orders-export';
import { shipmentStateLabel } from './shipment-state';
import { showsPaymentProofs } from './order-payment-proofs';
import { PAYABLE_TONE, payableStatusKey } from './despatch-payable';
import { addMoney, mulMoney, subMoney } from './shop/line-money';
import { plainAmount } from './retailer-budget';
import {
  REVISION_FEEDBACK_SUBJECT,
  type OrderDetail, type OrderSummary, type PaymentProof, type PaymentProofStatus, type ShipmentState,
} from '../api/types';
import {
  Alert, Button, Chip, Empty, Field, Money, Note, PageHeader, Panel, Rail, Spinner, Table, type Tone,
} from '../ui';
import { OrderChat } from '../ui/OrderChat';
import { ItemNames } from './ItemNames';
import { ChangeRequestsAdmin } from './order/ChangeRequestsAdmin';
import { BulkStatus } from './order/BulkStatus';
import { MilestonesPanel } from './order/MilestonesPanel';

const SHIPMENT_STATE_TONE: Record<ShipmentState, Tone> = {
  DRAFT: 'them', CONFIRMED: 'us', PACKED: 'us', AWAITING_PAYMENT: 'stop', DISPATCHED: 'run', DELIVERED: 'done', CANCELLED: 'lapse',
};

const STATUS_TONE: Record<string, Tone> = {
  SUBMITTED: 'them', UNDER_REVIEW: 'them', AWAITING_ACCEPTANCE: 'us', APPROVED: 'done',
  AWAITING_ADVANCE: 'us', IN_MANUFACTURING: 'run', READY: 'run', SHIPPED: 'run',
  DELIVERED: 'done', CANCELLED: 'lapse', REJECTED: 'stop', EXPIRED: 'lapse',
};

/** The actions this screen renders buttons for — the machine's admin-side
 *  transitions (`order.machine.ts`'s own table minus `RETAILER_ACTIONS`,
 *  mirrored here since the frontend has no reason to import backend code).
 *  Labels are the imperative a person clicks, not the action's identifier. */
const ADMIN_ACTIONS: Record<string, string> = {
  /* A-15: confirm or reject. `beginReview` is no longer offered — Screen Flow §6 has no such step; the
     API still accepts it, and an order already UNDER_REVIEW can be confirmed all the same. */
  confirm: 'Confirm order',
  /* `sendForAcceptance` and `requireAdvance` are deliberately ABSENT.
     Both were retired at v1.6 — there is no negotiation or acceptance step
     (Appendix B: OR-04, OR-13) and no advance under consignment (D33) — and the
     buttons are gated on `action in ADMIN_ACTIONS`, so leaving them out of this
     map is what stops them being offered. The backend still exposes the
     transitions; nothing in the portal walks a person into them.
     OR-02's chain is SUBMITTED -> CONFIRMED -> IN MANUFACTURING -> READY ->
     SHIPPED -> RECEIVED, and Screen Flow reduces A-15 to confirm or reject. */
  beginManufacture: 'Begin manufacturing',
  markReady: 'Mark ready',
  reject: 'Reject',
  cancelByMiraki: 'Cancel',
};

/**
 * A refused order action, said so a person can act on it. The server's own sentence (a 409 names the
 * rule that refused: budget, credit, account on hold, payment not verified) is shown as it is; a
 * bare "Validation failed" or "Conflict" says nothing, so those are replaced with what they mean.
 */
function actionError(e: unknown): string {
  if (!(e instanceof ApiError)) return 'That did not work. Check your connection and try again.';
  if (e.fieldErrors.length > 0) {
    return 'The server did not recognise that action, so nothing was changed. Refresh the page and try again; '
      + 'if it keeps happening, the system may be mid-update — ask for it to be restarted.';
  }
  if (e.status === 403) return 'You do not have permission to do that. Ask an administrator to grant it.';
  if (e.status === 409 && (!e.message || /^conflict$/i.test(e.message))) {
    return 'This order has changed since you opened it, so that step is no longer allowed. Refresh the page to see its current state.';
  }
  return e.message || 'That did not work';
}

/** Sentence-case label for a status code — `UNDER_REVIEW` reads "Under review". */
const statusLabel = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

/** A timestamp's calendar date in the VIEWER's zone, as `YYYY-MM-DD` — the
 *  same day the column shows, and the form a date input gives back. */
const localDay = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * A-14 — archetype 6: list, filter, row action.
 *
 * Filtered here, over the fetched list, not on the server: `GET /orders`
 * returns every order on the portal unpaged, so this is the whole set — not
 * the first page of it, which is the trap ProductList's own note describes.
 * If the endpoint ever pages, these must move to query parameters with it.
 */
export function Orders() {
  const { data, isLoading, error } = useOrders();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [company, setCompany] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  /** Kept across filter changes — narrowing the list does not drop what was picked. */
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  /** Orders whose lines have been fetched so far, while a download is being built. */
  const [exporting, setExporting] = useState<{ done: number; total: number } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const fetchDetails = useFetchOrderDetails();

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;

  const all = data ?? [];
  // Offered values come from the orders themselves, so a filter never lists
  // a status or company that would match nothing.
  const statuses = [...new Set(all.map((o) => o.status))].sort();
  const companies = [...new Set(all.map((o) => o.retailer))].sort((a, b) => a.localeCompare(b));
  const q = search.trim().toLowerCase();
  const rows = all.filter((o) => {
    if (q && !o.reference.toLowerCase().includes(q) && !(o.placedBy ?? '').toLowerCase().includes(q)) return false;
    if (status && o.status !== status) return false;
    if (company && o.retailer !== company) return false;
    const day = localDay(o.submittedAt);
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
  });
  const filtered = !!(q || status || company || from || to);
  const clear = () => { setSearch(''); setStatus(''); setCompany(''); setFrom(''); setTo(''); };

  // The header checkbox answers for the rows in front of you, as ProductList's does.
  const shownIds = rows.map((o) => o.id);
  const allShownSelected = shownIds.length > 0 && shownIds.every((id) => selected.has(id));
  const someShownSelected = !allShownSelected && shownIds.some((id) => selected.has(id));
  // In the queue's own order, and only orders still on the portal.
  const selectedOrders = all.filter((o) => selected.has(o.id));

  /**
   * An Orders sheet from the summaries already on screen, and a Lines sheet
   * from each order's detail — fetched here, since the list carries no lines.
   */
  async function download(orders: OrderSummary[], scope: 'all' | 'selected') {
    if (orders.length === 0 || exporting) return;
    setExportError(null);
    setExporting({ done: 0, total: orders.length });
    try {
      const details = await fetchDetails(
        orders.map((o) => o.id),
        (done) => setExporting({ done, total: orders.length }),
      );
      await downloadOrdersWorkbook(orders, details, exportFileName(scope));
    } catch (e) {
      setExportError(e instanceof ApiError ? e.message : 'The download could not be prepared.');
    } finally {
      setExporting(null);
    }
  }
  const exportLabel = exporting
    ? `Preparing… ${exporting.done} of ${exporting.total}`
    : null;

  return (
    <>
      <PageHeader title="Order queue"  />
      <div className="mk-toolbar">
        <input
          className="mk-fld mk-toolbar-search"
          value={search}
          placeholder="Search order or buyer"
          aria-label="Search order reference or buyer"
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={status} aria-label="Status" onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <select value={company} aria-label="Company" onChange={(e) => setCompany(e.target.value)}>
          <option value="">All companies</option>
          {companies.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="mk-toolbar-check">
          From
          <input
            type="date"
            className="mk-fld"
            value={from}
            max={to || undefined}
            aria-label="Submitted from"
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="mk-toolbar-check">
          To
          <input
            type="date"
            className="mk-fld"
            value={to}
            min={from || undefined}
            aria-label="Submitted to"
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
        {filtered ? (
          <>
            <span className="mk-dim">{rows.length} of {all.length}</span>
            <button type="button" className="mk-linkish" onClick={clear}>Clear filters</button>
          </>
        ) : null}
        <span className="mk-spacer" />
        {/* What is shown, which is every order until a filter narrows it — the
            label says which, so the file's scope is never a surprise. */}
        <Button
          size="sm"
          tone="ghost"
          disabled={rows.length === 0 || !!exporting}
          title="Excel file with an Orders sheet and a Lines sheet"
          onClick={() => void download(rows, 'all')}
        >
          {exportLabel ?? (filtered ? `Download shown (${rows.length})` : 'Download all')}
        </Button>
      </div>
      {exportError ? <Alert tone="stop">{exportError}</Alert> : null}
      {selected.size > 0 ? (
        <div className="mk-bulkbar">
          <b className="mk-bulkbar-count">{selectedOrders.length} {selectedOrders.length === 1 ? 'order' : 'orders'} selected</b>
          <button type="button" className="mk-linkish" onClick={() => setSelected(new Set())}>Clear selection</button>
          <span className="mk-spacer" />
          <BulkStatus
            orders={selectedOrders}
            onDone={(moved) => setSelected((prev) => new Set([...prev].filter((id) => !moved.includes(id))))}
          />
          <Button
            size="sm"
            disabled={selectedOrders.length === 0 || !!exporting}
            title="Excel file with an Orders sheet and a Lines sheet"
            onClick={() => void download(selectedOrders, 'selected')}
          >
            {exportLabel ?? 'Download selected'}
          </Button>
        </div>
      ) : null}
      <Panel>
        <Table<OrderSummary>
          rows={rows}
          rowKey={(o) => o.id}
          rowClassName={(o) => (selected.has(o.id) ? 'on' : undefined)}
          empty={filtered
            ? <Empty title="Nothing matches" body="No order on this portal answers to those filters." />
            : <Empty title="No orders yet" body="A submitted order appears here." />}
          // Every column centred, header and value alike, with widths set so
          // the gaps between columns are even — the same treatment as the
          // retailer's cart review table.
          columns={[
            {
              header: (
                <input
                  type="checkbox"
                  className="mk-rowcheck"
                  checked={allShownSelected}
                  ref={(el: HTMLInputElement | null) => { if (el) el.indeterminate = someShownSelected; }}
                  aria-label="Select all shown"
                  onChange={() => setSelected((prev) => {
                    const next = new Set(prev);
                    for (const id of shownIds) {
                      if (allShownSelected) next.delete(id); else next.add(id);
                    }
                    return next;
                  })}
                />
              ),
              width: '4%', center: true,
              render: (o) => (
                <input
                  type="checkbox"
                  className="mk-rowcheck"
                  checked={selected.has(o.id)}
                  aria-label={`Select ${o.reference}`}
                  onChange={() => setSelected((prev) => {
                    const next = new Set(prev);
                    if (next.has(o.id)) next.delete(o.id); else next.add(o.id);
                    return next;
                  })}
                />
              ),
            },
            {
              header: 'Order No.', width: '11%', center: true,
              // Both lines link to the order. The reference is the identifier people read, quote
              // and reach for — it was the one part of the cell that was not clickable. The row
              // itself is deliberately NOT a click target: it carries the selection checkbox, and
              // a row-wide handler fighting that checkbox is its own defect.
              render: (o) => (
                <>
                  <Link to={`/orders/${o.id}`}>{o.placedBy ?? '—'}</Link>
                  <div className="mk-dim"><Link to={`/orders/${o.id}`}>{o.reference}</Link></div>
                </>
              ),
            },
            { header: 'Item Name', width: '13%', center: true, render: (o) => <ItemNames names={o.items} /> },
            {
              // Total pieces to make, with how many different variants they
              // span under it — twelve of one design is a different job from
              // twelve different designs. Words in full, not "pcs"/"lines".
              header: 'Item Pcs', width: '8%', center: true,
              render: (o) => (
                <div title={`${o.pieces} ${o.pieces === 1 ? 'piece' : 'pieces'} in total, across ${o.lines} different ${o.lines === 1 ? 'variant' : 'variants'}`}>
                  <div className="mk-qty-main">{o.pieces} {o.pieces === 1 ? 'piece' : 'pieces'}</div>
                  <div className="mk-qty-sub">{o.lines} {o.lines === 1 ? 'variant' : 'variants'}</div>
                </div>
              ),
            },
            {
              header: 'Status', width: '14%', center: true,
              render: (o) => (
                <>
                  <ReadinessChip status={o.status} production={o.production} tone={STATUS_TONE[o.status] ?? 'lapse'} />
                  {/* OR-14 — the retailer wrote last and no one has replied. */}
                  {o.awaitingReply ? <div><Chip tone="us">Awaiting reply</Chip></div> : null}
                </>
              ),
            },
            { header: 'Company', width: '12%', center: true, render: (o) => o.retailer },
            {
              header: 'Submitted Price', width: '12%', center: true,
              render: (o) => <Money amount={o.submittedTotal} currency={o.currency} />,
            },
            {
              header: 'Agreed Price', width: '12%', center: true,
              render: (o) => <Money amount={o.agreedTotal} currency={o.currency} />,
            },
            {
              header: 'Submitted at', width: '14%', center: true,
              render: (o) => <span className="mk-dim">{new Date(o.submittedAt).toLocaleDateString()}</span>,
            },
          ]}
        />
      </Panel>
    </>
  );
}

/** A-15 — order detail: lines, the machine's available actions, and the
 *  payment proofs against it (folded in here rather than a separate A-21/
 *  A-22 screen — see the feasibility study's own scope note on this). */
/** A money amount as typed: digits, up to four decimals, never negative — `zMoney`'s pattern without the sign. */
const MONEY_INPUT = /^\d{1,15}(\.\d{1,4})?$/;

/** One line's editable values while the lines are being revised, held as typed. */
interface LineDraft { price: string; qty: string; size: string; remark: string }

const draftOf = (l: OrderDetail['lines'][number]): LineDraft => ({
  price: plainAmount(l.agreedUnitPrice), qty: String(l.quantity), size: l.size ?? '', remark: l.remark ?? '',
});

/** Numerically equal amounts ("95" and "95.0000") are not a change. */
const sameAmount = (a: string, b: string) => mulMoney(a, 1) === mulMoney(b, 1);

/**
 * Only what changed, per line — so saving a price edit does not also rewrite
 * a size nobody touched. Null means a draft does not validate yet.
 */
function revisionsFrom(lines: OrderDetail['lines'], drafts: Record<number, LineDraft>): LineRevision[] | null {
  const out: LineRevision[] = [];
  for (const l of lines) {
    const d = drafts[l.lineNumber];
    if (!d) continue;
    const price = d.price.trim();
    const qty = Number(d.qty);
    if (!MONEY_INPUT.test(price) || !Number.isInteger(qty) || qty < 1) return null;
    const r: LineRevision = { lineNumber: l.lineNumber };
    if (!sameAmount(price, l.agreedUnitPrice)) r.agreedUnitPrice = price;
    if (qty !== l.quantity) r.quantity = qty;
    if (d.size.trim() !== (l.size ?? '')) r.size = d.size.trim() || null;
    if (d.remark.trim() !== (l.remark ?? '')) r.remark = d.remark.trim() || null;
    if (Object.keys(r).length > 1) out.push(r);
  }
  return out;
}

export function OrderDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // The admin console is English by decision (D47, CR-02) but its copy is still a key, not a
  // literal: "Working…" was typed twice on this screen, which is one typo away from two spellings
  // of the same state.
  const { t } = useTranslation();
  const { data: o, isLoading, error } = useOrder(id);
  const { data: payments } = useOrderPaymentsAdmin(id);
  const { data: invoices } = useOrderConsignmentInvoices(id);
  // READY orders that still have a piece no live consignment invoice has claimed. An order whose
  // every piece is already invoiced is not in this list, so "Create Consignment Invoice" must not
  // be offered.
  const { data: candidates } = useConsignmentInvoiceCandidates();
  // Pieces already made and not yet invoiced can be invoiced while the order is still in manufacturing.
  const hasInvoiceable = (candidates ?? []).some((c) => c.orderId === id);
  const transition = useOrderTransition(id ?? '');
  const verify = useVerifyPayment(id ?? '');
  const sendMessage = useSendOrderMessage(id ?? '');
  const reviseLines = useReviseOrderLines(id ?? '');
  const { can, offers } = useSession();
  const { data: list } = useOrders();
  const [failed, setFailed] = useState<string | null>(null);
  /** Line values being revised, by line number — null when not editing. */
  const [lineDrafts, setLineDrafts] = useState<Record<number, LineDraft> | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [reasonFor, setReasonFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [rejectingProof, setRejectingProof] = useState<string | null>(null);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!o) return null;

  async function run(fn: () => Promise<unknown>) {
    setFailed(null);
    try { await fn(); } catch (e) { setFailed(actionError(e)); }
  }

  const needsReason = (action: string) => action === 'reject' || action === 'cancelByMiraki';

  // Terms change only while the order is in review — the same rule the API
  // enforces (`lockForReview`), so the edit buttons and the refusal agree.
  const editable = (o.status === 'SUBMITTED' || o.status === 'UNDER_REVIEW') && can('order.reprice');
  const revisions = lineDrafts ? revisionsFrom(o.lines, lineDrafts) : null;
  // The retailer's latest "send back" feedback, still unanswered.
  const feedback = o.queries.find((q) => q.subject === REVISION_FEEDBACK_SUBJECT && q.status === 'OPEN');
  const feedbackText = feedback?.messages.filter((m) => m.side === 'retailer').at(-1)?.body;

  /** While editing, the totals follow the drafts, so the effect of a change is visible before saving. */
  const draftLineTotal = (l: OrderDetail['lines'][number]) => {
    const d = lineDrafts?.[l.lineNumber];
    const qty = Number(d?.qty);
    if (!d || !MONEY_INPUT.test(d.price.trim()) || !Number.isInteger(qty) || qty < 1) return l.lineTotal;
    return mulMoney(d.price.trim(), qty);
  };
  const agreedPreview = lineDrafts ? o.lines.reduce((sum, l) => addMoney(sum, draftLineTotal(l)), '0') : o.agreedTotal;
  /** Terms → Variance: Submitted total − Agreed total, signed (negative when the
   *  agreed total is higher). Taken from the two figures shown beside it, not
   *  from `variancePct` — that is RL-07's line-level discount ceiling check. */
  const termsVariance = o.submittedTotal != null && agreedPreview != null ? subMoney(o.submittedTotal, agreedPreview) : null;

  async function downloadThis(order: OrderDetail) {
    setFailed(null);
    setDownloading(true);
    try {
      const placedBy = list?.find((x) => x.id === order.id)?.placedBy ?? null;
      await downloadOrdersWorkbook(
        [summaryFromDetail(order, placedBy)], new Map([[order.id, order]]), exportFileName({ reference: order.reference }),
      );
    } catch {
      setFailed('The download could not be prepared.');
    } finally {
      setDownloading(false);
    }
  }

  const cellInput = (
    l: OrderDetail['lines'][number], field: keyof LineDraft, props: { width: number; numeric?: boolean; label: string },
  ) => (
    <input
      className="mk-fld"
      style={{ width: props.width, ...(props.numeric ? { textAlign: 'right' as const } : {}) }}
      inputMode={props.numeric ? 'decimal' : undefined}
      aria-label={`${props.label}, line ${l.lineNumber}`}
      value={lineDrafts?.[l.lineNumber]?.[field] ?? ''}
      onChange={(e) => setLineDrafts((prev) => (prev ? {
        ...prev, [l.lineNumber]: { ...(prev[l.lineNumber] ?? draftOf(l)), [field]: e.target.value },
      } : prev))}
    />
  );

  return (
    <>
      {/* A detail screen reached from a list needs a way back to it. Browser back
          works, but a person who arrived from a link, a bookmark or a pasted URL
          has no history to go back through. */}
      <div className="mk-backlink">
        <Link to="/orders">&larr; Order queue</Link>
      </div>
      <PageHeader
        title={o.reference}
        sub={`${o.retailer.name} · submitted ${new Date(o.submittedAt).toLocaleDateString()}`}
        actions={(
          <>
            <Button
              size="sm"
              tone="ghost"
              disabled={downloading}
              title="Excel file with an Orders sheet and a Lines sheet"
              onClick={() => void downloadThis(o)}
            >
              {downloading ? 'Preparing…' : 'Download Excel'}
            </Button>
            <ReadinessChip status={o.status} production={o.production} tone={STATUS_TONE[o.status] ?? 'lapse'} />
          </>
        )}
      />
      {failed ? <Alert tone="stop">{failed}</Alert> : null}
      {o.expired ? <Alert tone="us">The acceptance window has closed on this order.</Alert> : null}
      {feedbackText && o.status === 'UNDER_REVIEW' ? (
        <Alert tone="us">
          <b>The retailer sent this offer back for revision.</b> “{feedbackText}” — revise the lines
          below, then send it for acceptance again. You can also reply in the conversation below.
        </Alert>
      ) : null}
      {o.closureReason ? <Note>Reason given when this order closed: {o.closureReason}</Note> : null}

      <Panel
        title={`${o.lines.length} line${o.lines.length === 1 ? '' : 's'}`}
        actions={editable ? (
          lineDrafts ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="sm"
                disabled={!revisions || revisions.length === 0 || reviseLines.isPending}
                {...(!revisions ? { title: 'Each line needs a price (up to 4 decimals) and a whole quantity of at least 1.' } : {})}
                onClick={() => void run(async () => {
                  if (revisions && revisions.length) await reviseLines.mutateAsync(revisions);
                  setLineDrafts(null);
                })}
              >
                Save lines
              </Button>
              <Button size="sm" tone="ghost" onClick={() => setLineDrafts(null)}>Cancel</Button>
            </div>
          ) : (
            <Button
              size="sm"
              tone="ghost"
              onClick={() => setLineDrafts(Object.fromEntries(o.lines.map((l) => [l.lineNumber, draftOf(l)])))}
            >
              Edit lines
            </Button>
          )
        ) : null}
      >
        <Table
          rows={o.lines}
          rowKey={(l) => String(l.lineNumber)}
          columns={[
            {
              header: 'Item / Product Name', width: '28%',
              render: (l) => (
                <div className="mk-piece">
                  <span className="mk-thumb">
                    {l.imageUrl ? <img src={l.imageUrl} alt="" /> : null}
                  </span>
                  <div>
                    <div className="mk-piece-name">{l.description ?? '—'}</div>
                    {lineDrafts
                      ? <div style={{ marginTop: 4 }}>{cellInput(l, 'remark', { width: 220, label: 'Remark' })}</div>
                      : (l.remark ? <div className="mk-dim mk-piece-remark">{l.remark}</div> : null)}
                  </div>
                </div>
              ),
            },
            { header: 'Variant', width: '21%', render: (l) => <code className="mk-piece-code">{l.variantCode}</code> },
            {
              header: 'Size', width: '8%',
              render: (l) => (lineDrafts ? cellInput(l, 'size', { width: 70, label: 'Size' }) : l.size ?? '—'),
            },
            {
              header: 'Pieces', numeric: true, width: '8%',
              render: (l) => (lineDrafts ? cellInput(l, 'qty', { width: 70, numeric: true, label: 'Quantity' }) : l.quantity),
            },
            { header: 'Submitted', numeric: true, width: '11%', render: (l) => <Money amount={l.submittedUnitPrice} currency={o.currency} /> },
            {
              header: 'Agreed', numeric: true, width: '12%',
              render: (l) => (lineDrafts
                ? cellInput(l, 'price', { width: 110, numeric: true, label: 'Agreed unit price' })
                : <Money amount={l.agreedUnitPrice} currency={o.currency} plain />),
            },
            // While the order is in production: how much of THIS product is made, and how much is not.
            ...(o.production ? [
              { header: 'Ready', numeric: true, width: '7%', render: (l: OrderDetail['lines'][number]) => l.made ?? 0 },
              { header: 'Yet to be ready', numeric: true, width: '9%', render: (l: OrderDetail['lines'][number]) => Math.max(0, l.quantity - (l.made ?? 0)) },
            ] : []),
            { header: 'Total price', numeric: true, width: '12%', render: (l) => <Money amount={draftLineTotal(l)} currency={o.currency} /> },
          ]}
        />
      </Panel>

      <Panel title="Terms" pad>
        <Rail items={[
          ['Submitted total', <Money key="s" amount={o.submittedTotal} currency={o.currency} />],
          ['Agreed total', <Money key="a" amount={agreedPreview} currency={o.currency} />],
          ['Variance', <Money key="v" amount={termsVariance} currency={o.currency} />],
          ['Expected delivery', o.expectedDeliveryDate ?? 'Not yet estimated'],
        ]}
        />
      </Panel>

      <Panel title="Actions" pad>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {o.availableActions.filter((a) => a in ADMIN_ACTIONS).length === 0 && !o.availableActions.includes('ship') && !hasInvoiceable ? (
            <Note>Nothing to do here — this order is either waiting on the retailer or in a closed state.</Note>
          ) : null}
          {hasInvoiceable ? (
            <Button onClick={() => void navigate(`/consignment-invoices/new?order=${encodeURIComponent(o.id)}`)}>
              Create Consignment Invoice
            </Button>
          ) : null}
          {/* Every piece is already on a consignment invoice: say so rather than leave a dead button. */}
          {o.availableActions.includes('ship') && candidates && !candidates.some((c) => c.orderId === o.id) ? (
            <Note>
              Every piece of this order is already on a consignment invoice
              {invoices && invoices.length > 0 ? ` (${invoices.map((i) => i.reference).join(', ')})` : ''}.
              Cancel an invoice to release its pieces.
            </Note>
          ) : null}
          {o.availableActions.filter((a) => a in ADMIN_ACTIONS).map((action) => (
            <Button
              key={action}
              tone={action === 'reject' || action === 'cancelByMiraki' ? 'ghost' : 'solid'}
              disabled={transition.isPending}
              onClick={() => {
                if (needsReason(action)) { setReasonFor(action); return; }
                void run(() => transition.mutateAsync({ action }));
              }}
            >
              {transition.isPending && transition.variables?.action === action ? t('common.working') : ADMIN_ACTIONS[action]}
            </Button>
          ))}
        </div>
        {/*
          * Production, as Manufacturing has recorded it. Mark ready is Admin acting on this, so it
          * is shown first and the button only appears once something is made: right after Begin
          * manufacturing nothing has been, and offering it then let an order be called ready
          * that no piece of had been made.
          */}
        {o.status === 'IN_MANUFACTURING' && o.production ? (
          <Note>
            {o.production.made === 0
              ? <>Manufacturing has not recorded any pieces yet (0 of {o.production.ordered}). They record the quantity made on{' '}
                <Link to="/manufacturing">Operations → Manufacturing</Link>. Mark ready appears here once at least one piece is recorded.</>
              : o.production.made < o.production.ordered
                ? <>Manufacturing has recorded {o.production.made} of {o.production.ordered} pieces as made. You can mark the order ready now,
                  which makes the other {o.production.ordered - o.production.made} a shortfall, or wait for them on{' '}
                  <Link to="/manufacturing">Operations → Manufacturing</Link>.</>
                : <>Manufacturing has recorded all {o.production.ordered} pieces as made. Mark the order ready to release it for shipping.</>}
          </Note>
        ) : null}
        {/* MF-06 — say why Begin manufacturing is missing rather than leave it
            to look like an oversight. Only where it would otherwise be offered. */}
        {o.advanceOutstanding && (o.status === 'APPROVED' || o.status === 'AWAITING_ADVANCE') ? (
          <Note>
            Begin manufacturing becomes available once payment proofs covering the advance are verified.
          </Note>
        ) : null}
        {reasonFor ? (
          <div style={{ marginTop: 12 }}>
            <Field label={`Reason to ${ADMIN_ACTIONS[reasonFor]?.toLowerCase()}`}>
              <input className="mk-fld" value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Button
                disabled={reason.trim().length < 3 || transition.isPending}
                onClick={() => void run(() => transition.mutateAsync({ action: reasonFor, reason }))
                  .then(() => { setReasonFor(null); setReason(''); })}
              >
                {transition.isPending ? t('common.working') : 'Confirm'}
              </Button>
              <Button tone="ghost" onClick={() => { setReasonFor(null); setReason(''); }}>Cancel</Button>
            </div>
          </div>
        ) : null}
      </Panel>

      <ChangeRequestsAdmin orderId={o.id} currency={o.currency} canDecide={can('order.approve')} />

      {/* 30/30/30/10 — figures, invoice attribution and refund. Renders nothing for a legacy order. */}
      <MilestonesPanel
        orderId={o.id}
        orderStatus={o.status}
        invoices={invoices ?? []}
        canVerify={can('payment.verify')}
        canRefund={can('payment.refund')}
      />

      {/* Defect 27 — see `order-payment-proofs.ts`. Under consignment nothing is payable at
          order time (SH-06, D33), so the panel's "the retailer hasn't submitted one" empty state
          chases money nobody owes; it appears there only once a proof exists. COURIER proofs are
          live money (SH-13) and are still verified here, which is why the panel stays. */}
      {showsPaymentProofs(offers('memo'), (payments ?? []).length) ? (
        <Panel title="Payment proofs">
          <Table<PaymentProof>
            rows={payments ?? []}
            rowKey={(p) => p.id}
            empty={<Empty title="No payment proof yet" body="The retailer hasn't submitted one against this order." />}
            columns={[
              { header: 'Submitted', render: (p) => new Date(p.submittedAt).toLocaleDateString() },
              {
                header: 'Amount', numeric: true,
                render: (p) => (
                  <>
                    <Money amount={p.amount} currency={p.currency} />
                    {p.purpose === 'COURIER' ? <div className="mk-dim">Courier charge</div> : null}
                  </>
                ),
              },
              { header: 'Reference', render: (p) => p.bankReference ?? '—' },
              {
                header: 'Status',
                render: (p) => <Chip tone={PROOF_TONE[p.status]}>{p.status}</Chip>,
              },
              {
                header: 'File',
                render: (p) => (p.hasFile
                  ? <a href={`/api/payments/${p.id}/file`} target="_blank" rel="noreferrer">View</a>
                  : <span className="mk-dim">Not attached</span>),
              },
              {
                header: '',
                render: (p) => (p.status !== 'PENDING' ? (
                  p.status === 'REJECTED' ? <span className="mk-dim">{p.rejectionReason}</span> : null
                ) : (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Button onClick={() => void run(() => verify.mutateAsync({ proofId: p.id, decision: 'verify' }))}>
                      Verify
                    </Button>
                    <Button tone="ghost" onClick={() => setRejectingProof(p.id)}>Reject</Button>
                  </div>
                )),
              },
            ]}
          />
          {rejectingProof ? (
            <div style={{ marginTop: 12 }}>
              <Field label="Reason for rejecting this proof">
                <input className="mk-fld" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button
                  disabled={reason.trim().length < 3 || verify.isPending}
                  onClick={() => void run(() => verify.mutateAsync({ proofId: rejectingProof, decision: 'reject', reason }))
                    .then(() => { setRejectingProof(null); setReason(''); })}
                >
                  Confirm rejection
                </Button>
                <Button tone="ghost" onClick={() => { setRejectingProof(null); setReason(''); }}>Cancel</Button>
              </div>
            </div>
          ) : null}
        </Panel>
      ) : null}

      {invoices && invoices.length > 0 ? (
        <Panel title={`${invoices.length} consignment invoice${invoices.length === 1 ? '' : 's'}`}>
          <Table
            rows={invoices}
            rowKey={(r) => r.id}
            columns={[
              { header: 'Consignment invoice', render: (r) => <Link to={`/consignment-invoices/${r.id}`}>{r.reference}</Link> },
              {
                // The one tone-and-label map, so this chip cannot say something the invoice page
                // does not — and so `NOT_PAYABLE` cannot fall through to "Pending" (SH-13).
                header: 'Status',
                render: (r) => (r.state === 'CANCELLED'
                  ? <Chip tone="lapse">Cancelled</Chip>
                  : <Chip tone={PAYABLE_TONE[r.paymentStatus]}>{t(payableStatusKey(r.paymentStatus))}</Chip>),
              },
              {
                header: 'Shipment',
                render: (r) => (r.shipment
                  ? <span>{r.shipment.reference} <Chip tone={SHIPMENT_STATE_TONE[r.shipment.state]}>{shipmentStateLabel(r.shipment.state)}</Chip></span>
                  : <span className="mk-dim">—</span>),
              },
            ]}
          />
        </Panel>
      ) : null}

      {/* OR-14 — the order's conversation with the retailer, for its whole
          working life. Replying needs `order.query`, as answering did. */}
      <OrderChat
        messages={o.conversation}
        mySide="miraki"
        canSend={o.canMessage && can('order.query')}
        onSend={(body) => sendMessage.mutateAsync(body)}
        labels={{
          title: 'Conversation with the retailer',
          empty: 'No messages yet. Anything said here is kept on this order, for both sides to see.',
          placeholder: 'Write to the retailer — Enter to send, Shift+Enter for a new line',
          send: 'Send',
          sending: 'Sending…',
          closed: o.canMessage
            ? 'Replying needs the order.query permission.'
            : 'This order is finished. The conversation stays here to read.',
          failed: 'The message was not sent. Try again.',
        }}
      />
    </>
  );
}

const PROOF_TONE: Record<PaymentProofStatus, Tone> = { PENDING: 'us', VERIFIED: 'done', REJECTED: 'stop' };
